import { Box, Divider, Modal } from '@zillow/constellation-10';
import { handleSystemMessageCtaClick } from './handleSystemMessageCtaClick';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { AppContext } from '../../../contexts/AppContextProvider';
import { useSatelliteWebLandlordConversationHandler_markConversationAsRead } from '@zg-rentals/ts-satellite-client-react-query';
import { useConversationMessagesQuery } from '../../../hooks/useConversationMessagesQuery';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import useDevices from '../../../hooks/useDevices';
import useLeadManagementActions from '../../../hooks/useLeadManagementActions';
import useModal from '../../../hooks/useModal';
import useQueryParamsNext from '../../../hooks/useQueryParamsNext';
import { useCachedLeadByConversationId, useOptimisticLeadUpdate } from '../../../hooks/useLeadManagementTableQuery';
import { ModalType } from '../../../types/ModalType';
import RenterMessagingModalHeader from './components/RenterMessagingModalHeader';
import AddMessage from '../../AddMessage';
import MessageListActions from '../../MessageListActions';
import MessageListItems from '../../MessageListItems';
import type { OnSystemMessageCtaClickArgs } from '../../MessageItem';
import { useAuth } from '../../../AuthProvider';
import { emitClickOnViewInMessagesIcon } from '../../../constants/clickstreamEvents/leadManagementMessageWindow';
import { emitRenterProfileChatBubbleAboutMeClickEvent } from '../../../constants/clickstreamEvents/renterProfile';
import { TRIGGER_LOCATION } from '../../../constants/clickstreamEvents/globals';
import { modalBodyStyles, modalContentWrapperStyles } from './styles';

// Layout constants (drawer width must match ../Drawer/styles.ts)
const DRAWER_WIDTH = 720;
const DEFAULT_SPACING = 16; // matches Constellation 'default' spacing token
const MODAL_WIDTH = 380;
const MODAL_HEIGHT = 480;

type RenterMessagingModalProps = {
  conversationId: string;
};

export default function RenterMessagingModal({ conversationId }: RenterMessagingModalProps) {
  const { updateQueryParams } = useQueryParamsNext();
  const { isDesktopView } = useDevices();
  const { setActiveModal } = useModal();
  const { user } = useAuth();
  const { paths } = useContext(AppContext);
  const { conversationId: renterDetailsConversationId, clientTimeZone } = useContext(LeadManagementContext);
  const { onAddMessageCompletedCacheUpdate } = useLeadManagementActions({ conversationId });
  const lead = useCachedLeadByConversationId(conversationId);
  const { leadId } = lead?.renterInfo || {};
  const activeListingAlias = lead?.listingDetails?.listingAlias ?? '';
  const optimisticLeadUpdate = useOptimisticLeadUpdate();

  // Single fetch for the modal: MessageListItems below also subscribes to this same React Query key,
  // so the data is shared (no double-fetch) and the modal body and footer stay in sync.
  const { data: conversationData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
  });

  // Invalidate leadManagement:table on success so the unread red dot in the lead row
  // clears after the BE confirms. Also invalidate conversationCounts to keep the inbox
  // unread badge in sync with what the inbox flow does (see useConversationActions.tsx).
  const { mutate: markConversationAsRead } = useSatelliteWebLandlordConversationHandler_markConversationAsRead({
    meta: {
      invalidates: ['leadManagement:table', 'satellite:conversationCounts'],
      context: {
        api: 'markConversationAsRead',
        details: 'RenterMessagingModal - marking conversation as read on scroll-to-bottom',
      },
    },
  });
  const hasMarkedAsReadRef = useRef(false);
  const lastMarkedMessageIdRef = useRef<string | null>(null);

  const fullMessageCountNumber = conversationData?.pagination.queryFullMessageCount ?? 0;
  const hasQuickResponses = conversationData?.hasQuickResponses ?? false;
  const quickResponses = conversationData?.quickResponses ?? null;
  const isConversationBlockedByZillow = conversationData?.isConversationBlockedByZillow ?? false;
  // `isPhoneLead` isn't returned by `getMessages`; pull it from the lead in cache instead.
  const isPhoneLead = lead?.latestContact?.isPhoneLead ?? false;
  const isSpam = conversationData?.status?.isSpam ?? false;

  // The latest server-side message id in the conversation. Optimistic temp messages
  // (`temp-…`, set by AddMessage while a send is in flight) are excluded so a user-sent
  // message in flight doesn't trigger the mark-as-read guard reset.
  const latestServerMessageId =
    conversationData?.conversation
      ?.slice()
      .reverse()
      .find((m) => !m.messageId?.startsWith('temp-'))?.messageId ?? null;

  // When a new message arrives via WebSocket while the modal is open, the latest
  // message id changes — reset the one-shot guard so the next scroll-to-bottom marks
  // the new message as read. (We track the latest id rather than `pagination.queryFullMessageCount`
  // because that field is not reliably populated by the getMessages endpoint.)
  useEffect(() => {
    if (lastMarkedMessageIdRef.current === null) return;
    if (latestServerMessageId && latestServerMessageId !== lastMarkedMessageIdRef.current) {
      hasMarkedAsReadRef.current = false;
    }
  }, [latestServerMessageId]);

  const openRenterDetailsDrawer = useCallback(() => {
    updateQueryParams({ queryParamValues: { conversationId }, shouldReplaceHistory: true });
  }, [updateQueryParams, conversationId]);

  const handleScrollComplete = useCallback(() => {
    if (hasMarkedAsReadRef.current) return;
    hasMarkedAsReadRef.current = true;
    lastMarkedMessageIdRef.current = latestServerMessageId;

    const rollback = optimisticLeadUpdate(conversationId, (lead) => ({
      ...lead,
      latestContact: { ...lead.latestContact, isRead: true },
    }));

    markConversationAsRead(
      { postData: { conversationId, lastReadMs: Date.now() } },
      {
        onError: () => {
          hasMarkedAsReadRef.current = false;
          rollback();
        },
      },
    );
  }, [markConversationAsRead, conversationId, optimisticLeadUpdate, latestServerMessageId]);

  const handleRenterProfileClick = useCallback(() => {
    emitRenterProfileChatBubbleAboutMeClickEvent({
      user,
      listing: { listingAlias: activeListingAlias },
      triggerLocation: TRIGGER_LOCATION.LeadManagementPage,
      lead: { leadId },
    });
    openRenterDetailsDrawer();
  }, [user, activeListingAlias, leadId, openRenterDetailsDrawer]);

  const onSystemMessageCtaClick = useCallback(
    (args: OnSystemMessageCtaClickArgs) => {
      handleSystemMessageCtaClick(args, openRenterDetailsDrawer, paths?.leadManagementPage);
    },
    [openRenterDetailsDrawer, paths?.leadManagementPage],
  );

  // Don't render if Renter Details drawer is open and screen is smaller than desktop breakpoint
  const isRenterDetailsDrawerOpen = !!renterDetailsConversationId;
  if (isRenterDetailsDrawerOpen && !isDesktopView) {
    return null;
  }

  const handleClose = () => {
    updateQueryParams({
      queryParamValues: {
        messagingConversationId: null,
      },
      shouldReplaceHistory: true,
    });
  };

  const handleOpenInInbox = () => {
    emitClickOnViewInMessagesIcon(user, { listingAlias: activeListingAlias }, { leadId });
  };

  // Position modal to the left of the Renter Details drawer when it's open
  const rightPosition = isRenterDetailsDrawerOpen ? DRAWER_WIDTH + DEFAULT_SPACING : DEFAULT_SPACING;

  const bodyContent = (
    <MessageListItems
      conversation={{ conversationId, listingAlias: activeListingAlias, clientTimeZone }}
      leadId={leadId}
      onRenterProfileClick={handleRenterProfileClick}
      onScrollComplete={handleScrollComplete}
      onSystemMessageCtaClick={onSystemMessageCtaClick}
      triggerLocation={TRIGGER_LOCATION.LeadManagementPage}
    />
  );

  const footerContent = (
    <AddMessage
      variant="compact"
      conversationId={conversationId}
      listingAlias={activeListingAlias}
      leadId={leadId}
      clientTimeZone={clientTimeZone}
      fullMessageCountNumber={fullMessageCountNumber}
      hasQuickResponses={hasQuickResponses}
      quickResponses={quickResponses}
      isConversationBlockedByZillow={isConversationBlockedByZillow}
      isPhoneLead={isPhoneLead}
      isSpam={isSpam}
      triggerLocation={TRIGGER_LOCATION.LeadManagementPage}
      onAddMessageCompletedCacheUpdate={onAddMessageCompletedCacheUpdate}
    />
  );

  return (
    <Modal.Root open useDismissProps={{ escapeKey: false, outsidePress: false }}>
      <Modal.Portal>
        <Modal.Content
          style={{
            // NOTE: We need to use the style prop to set the right position because the width css prop can't be overriden in Modal.Content
            right: `${rightPosition}px`,
            // Keep the widget below full-screen modals (e.g. image preview) that use the 'modal' z-index token
            zIndex: 1000,
          }}
          css={{
            position: 'fixed',
            bottom: `${DEFAULT_SPACING}px`,
            width: `${MODAL_WIDTH}px`,
            height: `${MODAL_HEIGHT}px`,
            maxWidth: `${MODAL_WIDTH}px`,
            animation: 'none',
          }}
        >
          <Modal.Header css={{ paddingRight: 'default', paddingBlock: 'tight' }}>
            <RenterMessagingModalHeader
              conversationId={conversationId}
              clientTimeZone={clientTimeZone}
              listingAlias={activeListingAlias}
              onClose={handleClose}
              onOpenInInbox={handleOpenInInbox}
            />
          </Modal.Header>
          <Divider />
          <Box css={modalContentWrapperStyles} onDragEnter={() => setActiveModal(ModalType.AttachmentsUploader)}>
            <Modal.Body css={modalBodyStyles}>{bodyContent}</Modal.Body>
            <Box css={{ flexShrink: 0, marginTop: '-layout.looser' }}>
              <MessageListActions
                conversationId={conversationId}
                listingAlias={activeListingAlias}
                leadId={leadId}
                clientTimeZone={clientTimeZone}
                variant="compact"
                triggerLocation={TRIGGER_LOCATION.LeadManagementPage}
              />
            </Box>
            <Box css={{ flexShrink: 0 }}>
              <Divider />
            </Box>
            <Modal.Footer>{footerContent}</Modal.Footer>
          </Box>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
}
